// ── Agent Types ────────────────────────────────────────────────────────────────
//
// Domain types and API contracts — what clients send (AgentCreate) and receive (Agent).
// No Mongoose, no DB details. Those live in agent.model.ts.

import { z } from "zod";
import { Profile } from "./profile.type";

// ── Feedback enums ────────────────────────────────────────────────────────────

export const FeedbackType = {
  OPERATOR_EVALUATION: "operator_evaluation_added",
  USER_CONVERSATION: "user_conversation_feedback",
  USER_MESSAGE_EVALUATION: "user_message_evaluation",
} as const;

export const FeedbackStatus = {
  UNPROCESSED: "unprocessed",
  REVIEWED: "reviewed",
  ANNOTATED: "annotated",
} as const;

export const FeedbackEvaluation = {
  POSITIVE: "positive",
  NEGATIVE: "negative",
} as const;

// ── AgentFeedback ─────────────────────────────────────────────────────────────

export const AgentFeedback = z.object({
  id: z.string().describe("Unique resource identifier"),
  agent_id: z.string().min(1).describe("Parent agent ID"),
  team_id: z.string().min(1).describe("Owning team ID"),
  created_by: z.string().min(1).describe("Creator user ID"),
  type: z.enum(Object.values(FeedbackType) as [string, ...string[]]).describe("Feedback type"),
  status: z
    .enum(Object.values(FeedbackStatus) as [string, ...string[]])
    .describe("Processing status"),
  evaluation: z
    .enum(Object.values(FeedbackEvaluation) as [string, ...string[]])
    .describe("Positive or negative evaluation"),
  comment: z.string().min(1).describe("Feedback comment text"),
  activity_id: z.string().optional().describe("Optional link to a specific activity record"),
  created_at: z.coerce.date().describe("Creation timestamp"),
  updated_at: z.coerce.date().describe("Last update timestamp"),
  // ── Computed embed fields — present only when ?embed= is requested ──────────
  /** NOT stored. Resolved inline by the repo aggregation pipeline when ?embed=agent. */
  // z.lazy() is required because Agent is declared after AgentFeedback in this file.
  agent: z
    .lazy(() => Agent.partial())
    .optional()
    .describe("Embedded agent — present only when ?embed=agent"),
});
export type AgentFeedback = z.infer<typeof AgentFeedback>;

// Create — agent_id injected from URL param; status always "unprocessed" on create
export const AgentFeedbackCreate = AgentFeedback.omit({
  id: true,
  agent_id: true,
  created_at: true,
  updated_at: true,
  status: true,
  agent: true, // embed field — computed by the repo, never sent by the client
});
export type AgentFeedbackCreate = z.infer<typeof AgentFeedbackCreate>;

// Update — immutable fields omitted; status becomes updatable (reviewed, annotated)
export const AgentFeedbackUpdate = AgentFeedbackCreate.omit({
  team_id: true,
  created_by: true,
})
  .partial()
  .extend({
    status: z.enum(Object.values(FeedbackStatus) as [string, ...string[]]).optional(),
  });
export type AgentFeedbackUpdate = z.infer<typeof AgentFeedbackUpdate>;

// ── SupervisorFeedback ────────────────────────────────────────────────────────
//
// Curated instruction produced by a supervisor (AI or human) after processing
// raw AgentFeedback. Distinct from AgentFeedback — this represents the output
// of the curation pipeline, not the raw user input.

export const SupervisorFeedback = z.object({
  id: z.string().describe("Unique resource identifier"),
  agent_id: z.string().min(1).describe("Parent agent ID"),
  team_id: z.string().min(1).describe("Owning team ID"),
  created_by: z.string().min(1).describe("Creator user ID"),
  feedback: z.string().min(1).describe("The curated instruction for the agent"),
  // Optional context links
  board_id: z.string().optional().describe("Board context where feedback originated"),
  agent_feedback_id: z.string().optional().describe("Source AgentFeedback that triggered this"),
  activity_id: z.string().optional().describe("Related activity log entry"),
  created_at: z.coerce.date().describe("Creation timestamp"),
  updated_at: z.coerce.date().describe("Last update timestamp"),
  // ── Computed embed fields — present only when ?embed= is requested ──────────
  /** NOT stored. Resolved inline by the repo aggregation pipeline when ?embed=agent. */
  // z.lazy() is required because Agent is declared after SupervisorFeedback in this file.
  agent: z
    .lazy(() => Agent.partial())
    .optional()
    .describe("Embedded agent — present only when ?embed=agent"),
  /** NOT stored. Resolved inline when ?embed=board.
   *  Uses z.record to avoid a circular type import (board.type.ts imports agent.type.ts). */
  board: z
    .record(z.string(), z.unknown())
    .optional()
    .describe("Embedded board — present only when ?embed=board"),
});
export type SupervisorFeedback = z.infer<typeof SupervisorFeedback>;

// Create — agent_id injected from URL param
export const SupervisorFeedbackCreate = SupervisorFeedback.omit({
  id: true,
  agent_id: true,
  created_at: true,
  updated_at: true,
  agent: true, // embed field — computed by the repo, never sent by the client
  board: true, // embed field — computed by the repo, never sent by the client
});
export type SupervisorFeedbackCreate = z.infer<typeof SupervisorFeedbackCreate>;

// Update — immutable fields omitted; only feedback, board_id, activity_id updatable
export const SupervisorFeedbackUpdate = SupervisorFeedbackCreate.omit({
  team_id: true,
  created_by: true,
}).partial();
export type SupervisorFeedbackUpdate = z.infer<typeof SupervisorFeedbackUpdate>;

// ── AgentKind ─────────────────────────────────────────────────────────────────

export const AgentKind = {
  KAIBANJS: "kaibanjs",
  CREWAI: "crewai",
  EXTERNAL: "external",
  A2A: "a2a",
  CLAUDE_CODE: "claude_code",
} as const;

// ── AgentStatus ───────────────────────────────────────────────────────────────

export const AgentStatus = {
  ACTIVE: "active",
  DRAFT: "draft",
  INACTIVE: "inactive",
  IDLE: "idle",
} as const;

// ── Subdocument types ─────────────────────────────────────────────────────────

export const AgentExample = z.object({
  input: z.record(z.string(), z.unknown()).optional().describe("Input data for the example"),
  output: z.string().describe("Expected output string"),
});
export type AgentExample = z.infer<typeof AgentExample>;

export const AgentResource = z.object({
  resource_id: z.string().min(1).describe("Resource identifier"),
  language: z.string().optional().describe("Language of the resource"),
  index_name: z.string().optional().describe("Index name for the resource"),
});
export type AgentResource = z.infer<typeof AgentResource>;

// ── Response DTO — Agent ───────────────────────────────────────────────────────

export const Agent = z.object({
  id: z.string().describe("Unique resource identifier"),
  name: z.string().min(1).max(100).describe("Agent display name"),
  description: z.string().min(1).describe("Agent purpose and capabilities"),
  team_id: z.string().min(1).describe("Owning team ID"),
  owner_id: z.string().min(1).describe("Owner user ID"),
  created_by: z.string().min(1).describe("Creator user ID"),
  type: z.enum(Object.values(AgentKind) as [string, ...string[]]).describe("Agent framework kind"),
  status: z
    .enum(Object.values(AgentStatus) as [string, ...string[]])
    .describe("Agent lifecycle status"),
  // Optional fields
  alias: z
    .string()
    .optional()
    .describe("URL-safe slug, auto-generated from name if not provided. Immutable after creation."),
  agent_team_alias: z.string().optional().describe("Team-specific alias for this agent"),
  monthly_budget: z.number().min(0).optional().describe("Monthly budget limit"),
  roles: z.array(z.string()).optional().describe("Agent roles list"),
  integrations: z.array(z.string()).optional().describe("Enabled integration IDs"),
  examples: z.array(AgentExample).optional().describe("Input/output examples"),
  resources: z.array(AgentResource).optional().describe("Linked knowledge resources"),
  config: z.record(z.string(), z.unknown()).optional().describe("Arbitrary agent configuration"),
  avatar_id: z.string().optional().describe("GridFS file ID for the agent's avatar image"),
  avatar_url: z.string().optional().describe("Signed avatar download URL — computed, never stored"),
  created_at: z.coerce.date().describe("Creation timestamp"),
  updated_at: z.coerce.date().describe("Last update timestamp"),
  // Derived fields — computed on read, never stored or accepted as input
  active_cards: z
    .number()
    .int()
    .min(0)
    .optional()
    .describe("Count of active (non-deleted) cards currently assigned to this agent."),
  boards_count: z
    .number()
    .int()
    .min(0)
    .optional()
    .describe("Count of boards where this agent appears in agent_ids. Computed on read."),
  // ── Computed embed fields — present only when ?embed= is requested ──────────
  /** NOT stored. Resolved inline by the repo aggregation pipeline when ?embed=owner. */
  owner: Profile.partial()
    .optional()
    .describe("Embedded owner profile — present only when ?embed=owner"),
});
export type Agent = z.infer<typeof Agent>;

// ── Create DTO ────────────────────────────────────────────────────────────────

export const AgentCreate = Agent.omit({
  id: true,
  created_at: true,
  updated_at: true,
  active_cards: true, // derived — not a client input
  boards_count: true, // derived — not a client input
  avatar_id: true, // set via POST /agents/:id/avatar, not inline
  avatar_url: true, // computed — never stored
  owner: true, // embed field — computed by the repo, never sent by the client
});
export type AgentCreate = z.infer<typeof AgentCreate>;

// ── Update DTO ────────────────────────────────────────────────────────────────
// Immutable fields omitted: team_id, owner_id, created_by, alias

export const AgentUpdate = AgentCreate.omit({
  team_id: true,
  owner_id: true,
  created_by: true,
  alias: true,
}).partial();
export type AgentUpdate = z.infer<typeof AgentUpdate>;

// ── Retire types ──────────────────────────────────────────────────────────────

export const AgentRetireAction = {
  DEACTIVATE: "deactivate",
  REASSIGN: "reassign",
} as const;
export type AgentRetireAction = (typeof AgentRetireAction)[keyof typeof AgentRetireAction];

export const AgentRetireBoardEntry = z.object({
  board_id: z.string().min(1).describe("Board ID to process on agent retirement"),
  action: z
    .enum(Object.values(AgentRetireAction) as [string, ...string[]])
    .describe(
      "What to do with cards assigned to this agent on the board: " +
        "'deactivate' removes the agent assignment, " +
        "'reassign' moves it to another agent",
    ),
  reassign_to_agent_id: z
    .string()
    .min(1)
    .optional()
    .describe("Target agent ID — required when action is 'reassign'"),
});
export type AgentRetireBoardEntry = z.infer<typeof AgentRetireBoardEntry>;

export const AgentRetireBody = z
  .object({
    boards: z
      .array(AgentRetireBoardEntry)
      .describe(
        "Per-board retirement instructions. External channels for each board are always deleted.",
      ),
  })
  .superRefine((val, ctx) => {
    val.boards.forEach((entry, i) => {
      if (entry.action === AgentRetireAction.REASSIGN && !entry.reassign_to_agent_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["boards", i, "reassign_to_agent_id"],
          message: "reassign_to_agent_id is required when action is 'reassign'",
        });
      }
    });
  });
export type AgentRetireBody = z.infer<typeof AgentRetireBody>;

export const AgentRetireResult = z.object({
  success: z.boolean().describe("Whether the retirement transaction completed successfully"),
  processed_boards: z.number().int().min(0).describe("Number of boards processed"),
  processed_cards: z
    .number()
    .int()
    .min(0)
    .describe("Number of cards updated (agent unassigned or reassigned)"),
  processed_channels: z.number().int().min(0).describe("Number of external channels deleted"),
});
export type AgentRetireResult = z.infer<typeof AgentRetireResult>;
